import { useAddressSystemStore } from '@/store/addressSystem'
import { useAppStore } from '@/store/app/app'
import { useBasketStore } from '@/store/basket/basket'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { useUserStore } from '@/store/user/user'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import { Success } from '@/api/results'

import { storage } from '@/services/storage'
import { STORAGE_KEY } from '@/services/storage/constants'

export const useBasketMethods = () => {
  const fetchBasket = async () => {
    const { fetchBasket } = useBasketStore()
    const route = useRoute()

    await fetchBasket()

    if (route.name !== 'basket') {
      await setBasketDeliveryMethod()
    }
  }

  // Установка адресов пользователя в зависимости от выбранного метода получения заказа
  const setBasketDeliveryMethod = async () => {
    const { isAuthenticated } = storeToRefs(useUserStore())

    const deliveryWidget = storage.get<SetDeliveryMethodPayload>(
      STORAGE_KEY.addressSystem.deliveryWidget,
    )

    if (!isAuthenticated.value || !deliveryWidget) return

    return deliveryWidget.delivery_method === DeliveryMethods.COURIER
      ? await setCourierDeliveryMethod()
      : await setPointsDeliveryMethod(deliveryWidget)
  }

  // Установка адреса пользователя для доставки
  const setCourierDeliveryMethod = async () => {
    const { $apiService } = useNuxtApp()
    const { setDeliveryMethod } = useBasketStore()
    const { showSnackbar } = useAppStore()

    const deliveryAddress = storage.get<DeliveryDataAddress>(
      STORAGE_KEY.addressSystem.deliveryAddress,
    )

    if (!deliveryAddress) return

    const [longitude, latitude] = deliveryAddress.coordinates

    const payload = {
      coords: { latitude, longitude },
      data: {
        selectedSuggestion: deliveryAddress.address?.address || null,
        house: deliveryAddress.address?.address?.house || '',
        notes: '',
        porch: '',
        floor: '',
        intercom: '',
        flat: '',
      },
    }

    // Создаём новый адрес, который пользователь установил до авторизации
    const address = await $apiService.addresses.createNewAddress(
      payload.coords,
      payload.data,
    )

    if (!(address instanceof Success)) {
      showSnackbar({ message: address.message })
      return
    }

    storage.remove(STORAGE_KEY.addressSystem.deliveryAddress)
    storage.remove(STORAGE_KEY.addressSystem.deliveryWidget)

    if (address.data.allow_redirect) return address.data

    // Устанавливаем полученный адрес на сервере если не происходит редирект на другой город
    await setDeliveryMethod({
      delivery_method: DeliveryMethods.COURIER,
      address_id: address.data.id,
    })
  }

  // Установка адреса пользователя для самовывоза/в ресторане
  const setPointsDeliveryMethod = async (
    deliveryWidget: SetDeliveryMethodPayload,
  ) => {
    const { data } = storeToRefs(useBasketStore())
    const { setDeliveryMethod } = useBasketStore()

    // Проверяем, что адрес в ответе корзины и адрес установленный в АС сторе совпадают
    const isSameAddressId =
      data.value.delivery.addresses[deliveryWidget.delivery_method] ===
      deliveryWidget.point_id

    // После запроса корзины, если пользователь авторизован и в сторе АС есть адрес, то устанавливаем на сервере данные о доставке
    if (deliveryWidget.order_address && !isSameAddressId) {
      const { delivery_method, point_id } = deliveryWidget

      await setDeliveryMethod({ delivery_method, point_id })

      storage.remove(STORAGE_KEY.addressSystem.deliveryAddress)
      storage.remove(STORAGE_KEY.addressSystem.deliveryWidget)
    }
  }

  const handleRedirectCity = async () => {
    const { isAuthenticated } = storeToRefs(useUserStore())
    const { setDeliveryMethod } = useBasketStore()
    const { changeDeliveryBasket, changeDeliveryWidget } =
      useAddressSystemStore()

    const route = useRoute()
    const router = useRouter()

    if (!route.query.addressId || !route.query.deliveryMethod) return

    const deliveryMethod = route.query.deliveryMethod as DeliveryMethods

    const orderAddress = String(route.query.orderAddress) || ''

    // В зависимости от выбранного метода доставки, получаем id адреса или точки
    const propertyKey =
      deliveryMethod === DeliveryMethods.COURIER ? 'address_id' : 'point_id'

    const payload: SetDeliveryMethodPayload = {
      delivery_method: deliveryMethod,
      [propertyKey]: Number(route.query.addressId),
    }

    // Если после редиректа пользователь авторизован, то сохраняем доставку на сервер, в ином случае сохраняем локально
    if (isAuthenticated.value) {
      await setDeliveryMethod(payload)
    } else {
      changeDeliveryWidget({ ...payload, order_address: orderAddress })
      changeDeliveryBasket({ ...payload, order_address: orderAddress })
    }

    await router.replace({ query: null })
  }

  return {
    fetchBasket,
    handleRedirectCity,
    setBasketDeliveryMethod,
  }
}
